import React, { useState, useMemo } from 'react';
import { SelectedMetric, MetricStats } from '../types';

interface MetricSelectorProps {
  metrics: SelectedMetric[];
  stats: Record<string, MetricStats>;
  onToggleMetric: (key: string) => void;
  onSelectAll: () => void;
  onDeselectAll: () => void;
  collapsed?: boolean;
  onToggleCollapse?: () => void;
  onHidePanel?: () => void;
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    background: 'rgba(33, 40, 44, 0.8)',
    borderRadius: '8px',
    border: '1px solid rgba(108, 117, 125, 0.3)',
    overflow: 'hidden',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0.6rem 0.75rem',
    borderBottom: '1px solid rgba(108, 117, 125, 0.2)',
    background: 'rgba(33, 40, 44, 0.5)',
  },
  title: {
    color: '#c5c5c5',
    fontSize: '0.8rem',
    fontWeight: 500,
    margin: 0,
  },
  actions: {
    display: 'flex',
    gap: '0.5rem',
  },
  actionButton: {
    padding: '0.2rem 0.5rem',
    borderRadius: '3px',
    border: '1px solid rgba(108, 117, 125, 0.3)',
    background: 'transparent',
    color: '#8b949e',
    fontSize: '0.7rem',
    cursor: 'pointer',
  },
  searchContainer: {
    padding: '0.5rem 0.75rem',
    borderBottom: '1px solid rgba(108, 117, 125, 0.1)',
  },
  searchInput: {
    width: '100%',
    padding: '0.35rem 0.5rem',
    borderRadius: '4px',
    border: '1px solid rgba(108, 117, 125, 0.3)',
    background: 'rgba(33, 40, 44, 0.95)',
    color: '#c5c5c5',
    fontSize: '0.8rem',
  },
  list: {
    padding: '0.5rem',
  },
  metricItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    padding: '0.4rem 0.5rem',
    borderRadius: '4px',
    cursor: 'pointer',
    transition: 'background 0.15s ease',
  },
  metricItemHover: {
    background: 'rgba(108, 117, 125, 0.1)',
  },
  checkbox: {
    width: '14px',
    height: '14px',
    cursor: 'pointer',
    accentColor: '#53bde4',
  },
  colorIndicator: {
    width: '10px',
    height: '10px',
    borderRadius: '2px',
    flexShrink: 0,
  },
  metricName: {
    flex: 1,
    color: '#c5c5c5',
    fontSize: '0.8rem',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
  },
  metricStats: {
    display: 'flex',
    gap: '0.75rem',
    fontSize: '0.7rem',
    color: '#6c757d',
  },
  statItem: {
    display: 'flex',
    gap: '0.25rem',
  },
  statLabel: {
    color: '#6c757d',
  },
  statValue: {
    color: '#8b949e',
  },
  groupHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    padding: '0.4rem 0.5rem',
    color: '#8b949e',
    fontSize: '0.75rem',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.5px',
    cursor: 'pointer',
  },
  expandIcon: {
    width: '12px',
    height: '12px',
    transition: 'transform 0.15s ease',
  },
  visibleCount: {
    marginLeft: 'auto',
    color: '#53bde4',
    fontSize: '0.7rem',
  },
};

export function MetricSelector({
  metrics,
  stats,
  onToggleMetric,
  onSelectAll,
  onDeselectAll,
  collapsed = false,
  onToggleCollapse,
  onHidePanel,
}: MetricSelectorProps) {
  const [search, setSearch] = useState('');
  const [hoveredMetric, setHoveredMetric] = useState<string | null>(null);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set(['_ungrouped']));

  // Filter metrics by search
  const filteredMetrics = useMemo(() => {
    if (!search.trim()) return metrics;
    const searchLower = search.toLowerCase();
    return metrics.filter(m =>
      m.key.toLowerCase().includes(searchLower) ||
      m.label.toLowerCase().includes(searchLower)
    );
  }, [metrics, search]);

  // Group metrics by prefix (e.g., cpu_*, gpu_*, etc.)
  const groupedMetrics = useMemo(() => {
    const groups: Record<string, SelectedMetric[]> = {};

    for (const metric of filteredMetrics) {
      const underscoreIndex = metric.key.indexOf('_');
      const prefix = underscoreIndex > 0 ? metric.key.substring(0, underscoreIndex) : '_ungrouped';

      if (!groups[prefix]) {
        groups[prefix] = [];
      }
      groups[prefix].push(metric);
    }

    // Sort groups alphabetically, but put _ungrouped last
    const sortedGroups = Object.entries(groups).sort(([a], [b]) => {
      if (a === '_ungrouped') return 1;
      if (b === '_ungrouped') return -1;
      return a.localeCompare(b);
    });

    return sortedGroups;
  }, [filteredMetrics]);

  const toggleGroup = (prefix: string) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(prefix)) {
        next.delete(prefix);
      } else {
        next.add(prefix);
      }
      return next;
    });
  };

  const formatStatValue = (value: number): string => {
    if (Math.abs(value) >= 1000) {
      return value.toFixed(0);
    }
    if (Math.abs(value) >= 100) {
      return value.toFixed(1);
    }
    return value.toFixed(2);
  };

  const visibleCount = metrics.filter(m => m.visible).length;

  if (collapsed) {
    return (
      <div style={styles.container}>
        <div
          style={{ ...styles.header, cursor: 'pointer' }}
          onClick={onToggleCollapse}
        >
          <h4 style={styles.title}>
            Metrics ({visibleCount}/{metrics.length})
          </h4>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#8b949e" strokeWidth="2">
            <path d="M9 18l6-6-6-6" />
          </svg>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <h4 style={styles.title}>
          Metrics
          <span style={styles.visibleCount}> ({visibleCount}/{metrics.length})</span>
        </h4>
        <div style={styles.actions}>
          <button style={styles.actionButton} onClick={onSelectAll}>
            All
          </button>
          <button style={styles.actionButton} onClick={onDeselectAll}>
            None
          </button>
          {onHidePanel && (
            <button
              style={styles.actionButton}
              onClick={onHidePanel}
              title="Hide panel"
            >
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M9 18l6-6-6-6" />
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* Search */}
      <div style={styles.searchContainer}>
        <input
          type="text"
          placeholder="Search metrics..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={styles.searchInput}
        />
      </div>

      {/* Metric list with groups */}
      <div style={styles.list}>
        {groupedMetrics.map(([prefix, groupMetrics]) => (
          <div key={prefix}>
            {/* Group header (only if there are multiple groups or not ungrouped) */}
            {groupedMetrics.length > 1 && prefix !== '_ungrouped' && (
              <div style={styles.groupHeader}>
                {prefix}_* ({groupMetrics.length})
              </div>
            )}

            {/* Metrics in group - always expanded */}
            {groupMetrics.map(metric => {
                const metricStats = stats[metric.key];
                return (
                  <div
                    key={metric.key}
                    style={{
                      ...styles.metricItem,
                      ...(hoveredMetric === metric.key ? styles.metricItemHover : {}),
                      paddingLeft: groupedMetrics.length > 1 && prefix !== '_ungrouped' ? '1.5rem' : '0.5rem',
                    }}
                    onMouseEnter={() => setHoveredMetric(metric.key)}
                    onMouseLeave={() => setHoveredMetric(null)}
                    onClick={() => onToggleMetric(metric.key)}
                  >
                    <input
                      type="checkbox"
                      checked={metric.visible}
                      onChange={() => onToggleMetric(metric.key)}
                      onClick={e => e.stopPropagation()}
                      style={styles.checkbox}
                    />
                    <div
                      style={{
                        ...styles.colorIndicator,
                        background: metric.visible ? metric.color : '#6c757d',
                        opacity: metric.visible ? 1 : 0.4,
                      }}
                    />
                    <span
                      style={{
                        ...styles.metricName,
                        opacity: metric.visible ? 1 : 0.6,
                      }}
                    >
                      {metric.label}
                    </span>
                    {metricStats && (
                      <div style={styles.metricStats}>
                        <span style={styles.statItem}>
                          <span style={styles.statLabel}>avg:</span>
                          <span style={styles.statValue}>{formatStatValue(metricStats.avg)}</span>
                        </span>
                        <span style={styles.statItem}>
                          <span style={styles.statLabel}>max:</span>
                          <span style={styles.statValue}>{formatStatValue(metricStats.max)}</span>
                        </span>
                      </div>
                    )}
                  </div>
                );
              })}
          </div>
        ))}

        {filteredMetrics.length === 0 && (
          <div style={{ color: '#6c757d', padding: '1rem', textAlign: 'center', fontSize: '0.8rem' }}>
            {search ? 'No metrics match your search' : 'No metrics available'}
          </div>
        )}
      </div>
    </div>
  );
}

export default MetricSelector;
